import { GoogleMap, Marker, DirectionsRenderer, Polygon } from '@react-google-maps/api';
import CustomMarker from './CustomMarker';
import { FC, useContext } from 'react';
import { HotelsMapProps } from 'ts/hotel/interfaces/Map';
import { Position } from 'ts/hotel/interfaces/Generic';
import { getCenteredHotel } from 'utils/hotel';
import { MapHotelContext } from 'contexts/MapHotelContext';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { imgBluePoint, imgRedPoint, imgWaypointB, imgWaypointA } from 'constants/generic';

const HotelsGoogle: FC<HotelsMapProps> = ({ center, zoom, to, from, ...props }) => {
  
  const { geoPolygons } = useContext(MapHotelContext);
  const setRoad = (from?: Position, to?: Position) => {
    if (from && to) {
      const DirectionsService = new google.maps.DirectionsService();

      DirectionsService.route(
        {
          origin: new google.maps.LatLng(from.lat, from.lng),
          destination: new google.maps.LatLng(to.lat, to.lng),
          travelMode: google.maps.TravelMode.DRIVING,
        },
        (driving, status) => {
          if (status === google.maps.DirectionsStatus.OK) {
            DirectionsService.route(
              {
                origin: new google.maps.LatLng(from.lat, from.lng),
                destination: new google.maps.LatLng(to.lat, to.lng),
                travelMode: google.maps.TravelMode.WALKING,
              },
              (walking, status) => {
                if (status === google.maps.DirectionsStatus.OK) {
                  props.setDirections({ driving, walking });
                } else {
                  console.error(`error fetching directions`);
                }
              }
            );
          } else {
            console.error(`error fetching directions`);
          }
        }
      );
    }
  };

  const onClickFrom = (pos?: Position) => {
    if (pos) {
      props.setFrom(pos);
      setRoad(pos, to);
    }
  };

  const onClickTo = (pos?: Position) => {
    if (pos) {
      props.setTo(pos);
      setRoad(from, pos);
    }
  };

  return (
    <GoogleMap
      mapContainerClassName="map"
      center={center}
      options={{
        zoom,
        streetViewControl: false,
        mapTypeControl: false,
      }}
    >
      <Marker position={props.centerMarker!} />
      {props.hotels?.map((hotel) => (
        <CustomMarker
          key={hotel.htuKey}
          icon={{ url: imgBluePoint }}
          zIndex={1}
          markerPosition={{
            lat: hotel.position?.latitude || 0,
            lng: hotel.position?.longitude || 0,
          }}
          onClickMarker={() => props.onClickMarker('hotel', getCenteredHotel(hotel))}
          hotel={hotel}
          setDirections={props.setDirections}
          onClickFrom={onClickFrom}
          onClickTo={onClickTo}
          focusedMarker={props.focusedMarker}
          setFocusedMarker={props.setFocusedMarker}
          markerType="hotel"
        />
      ))}

      {props.showPolygon &&
        geoPolygons?.map((pol, index) => (
          <Polygon
            key={index}
            visible={props.showPolygon}
            paths={pol}
            options={{
              strokeColor: '#FF0000',
              strokeOpacity: 0.8,
              strokeWeight: 2,
              fillOpacity: 0.1,
            }}
          />
        ))}

      {!!props.directions?.walking && (
        <DirectionsRenderer
          directions={props.directions.walking}
          options={{
            polylineOptions: { strokeColor: 'lime' },
            suppressMarkers: true,
          }}
        />
      )}
      {!!props.directions?.driving && (
        <DirectionsRenderer
          directions={props.directions.driving}
          options={{
            suppressMarkers: true,
          }}
        />
      )}

      {from && (
        <CustomMarker
          icon={{ url: imgWaypointA }}
          zIndex={3}
          markerPosition={from}
          onClickMarker={() => props.onClickMarker('ab', from)}
          setDirections={props.setDirections}
          onClickFrom={onClickFrom}
          onClickTo={onClickTo}
          focusedMarker={props.focusedMarker}
          setFocusedMarker={props.setFocusedMarker}
          markerType="ab"
        />
      )}
      {to && (
        <CustomMarker
          icon={{ url: imgWaypointB }}
          zIndex={3}
          markerPosition={to}
          onClickMarker={() => props.onClickMarker('ab', to)}
          setDirections={props.setDirections}
          onClickFrom={onClickFrom}
          onClickTo={onClickTo}
          focusedMarker={props.focusedMarker}
          setFocusedMarker={props.setFocusedMarker}
          markerType="ab"
        />
      )}

      {!!props.additionalCenter && (
        <CustomMarker
          icon={{ url: imgRedPoint }}
          zIndex={2}
          markerPosition={props.additionalCenter}
          onClickMarker={() => props.onClickMarker('red', props.additionalCenter)}
          setDirections={props.setDirections}
          onClickFrom={onClickFrom}
          onClickTo={onClickTo}
          focusedMarker={props.focusedMarker}
          setFocusedMarker={props.setFocusedMarker}
          additionalCenter={props.additionalCenter}
          markerType="red"
        />
      )}
    </GoogleMap>
  );
};

export default withErrorBoundary(HotelsGoogle, 'HotelsGoogle');
